// src/App.js - routes.
//
// * Leader pages share one layout route (the menu and the top bar stay mounted while
//   navigating); admin pages are loaded on demand so leaders on phones download less
//   (audit FL-41).
// * A visitor who is not logged in is sent to /login and comes back to the page they
//   wanted after logging in (FL-36); a wrong role is redirected to its home page.
// * Running uploads are tracked app-wide (UploadProvider, FL-10).
import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import MaintenanceBanner from './components/MaintenanceBanner';
import StagingBanner from './components/StagingBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import { MetaProvider, useMeta } from './contexts/MetaContext';
import Spinner from './components/ui/Spinner';
import { UploadProvider } from './components/leader/UploadManager';

// Layouts
import { AdminLayout } from './layouts/AdminLayout';
import { LeaderLayout } from './layouts/LeaderLayout';

// Leader pages
import LeaderDashboard from './pages/Dashboard';
import DancersList from './pages/DancersList';
import Competitions from './pages/Competitions';
import CompetitionDetail from './pages/CompetitionDetail';
import ChoreographiesList from './pages/ChoreographiesList';
import ChoreographyDetail from './pages/ChoreographyDetail';
import QualifiedChoreographies from './pages/QualifiedChoreographies';
import InvoiceManagement from './pages/InvoiceManagement';

// Auth pages
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword';

// Admin pages (separate chunks)
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const GroupsManagement = lazy(() => import('./pages/admin/GroupsManagement'));
const GroupDetail = lazy(() => import('./pages/admin/GroupDetail'));
const CompetitionsManagement = lazy(() => import('./pages/admin/CompetitionsManagement'));
const LogoManagement = lazy(() => import('./pages/admin/LogoManagement'));
const MediaManagement = lazy(() => import('./pages/admin/MediaManagement'));
const AdminChoreographyDetail = lazy(() => import('./pages/admin/ChoreographyDetail'));
const ExportPrint = lazy(() => import('./pages/admin/ExportPrint'));
const EarlyBirdReport = lazy(() => import('./pages/admin/EarlyBirdReport'));
const AdminInvoiceManagement = lazy(() => import('./pages/admin/InvoiceManagement'));
const AdminInvoiceDetail = lazy(() => import('./pages/admin/InvoiceDetail'));
const InvoiceSettings = lazy(() => import('./pages/admin/InvoiceSettings'));
const CompetitionOverview = lazy(() => import('./pages/admin/CompetitionOverview'));
const RunningOrder = lazy(() => import('./pages/admin/RunningOrder'));
const Qualification = lazy(() => import('./pages/admin/Qualification'));
const TourStatistics = lazy(() => import('./pages/admin/TourStatistics'));
const Outbox = lazy(() => import('./pages/admin/Outbox'));
const Guide = lazy(() => import('./pages/admin/Guide'));

/** A page of a switched-off feature behaves like an unknown address. */
const FeatureRoute = ({ name, children }) => {
  const meta = useMeta();
  if (meta.loaded && !meta.featureEnabled(name)) return <Navigate to="/admin" replace />;
  return children;
};

const homeFor = (user) => (user && user.role === 'admin' ? '/admin' : '/');

/** Where to go after login: the page the user wanted, if it belongs to their role. */
function returnPath(from, user) {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//') || from.startsWith('/login')) {
    return homeFor(user);
  }
  const isAdminPath = from === '/admin' || from.startsWith('/admin/');
  if (user.role === 'admin' ? !isAdminPath : isAdminPath) return homeFor(user);
  return from;
}

const RequireAuth = ({ children, roles = [] }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <Spinner label="Načítání…" />;

  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
};

const LoginRoute = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (user) return <Navigate to={returnPath(location.state && location.state.from, user)} replace />;
  return <Login />;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch (e) {
      /* not available (tests) */
    }
  }, [pathname]);
  return null;
};

/** Fresh page state (dialogs, drafts, errors) when only the :id in the URL changes. */
const KeyedById = ({ component: Component }) => {
  const { id } = useParams();
  return <Component key={id} />;
};

const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path="/login" element={<LoginRoute />} />
    <Route path="/reset-password/:token" element={<ResetPassword />} />

    {/* Leader area (an admin opening "/" is sent to /admin) */}
    <Route
      element={
        <RequireAuth roles={['leader']}>
          <LeaderLayout>
            <Outlet />
          </LeaderLayout>
        </RequireAuth>
      }
    >
      <Route index element={<LeaderDashboard />} />
      <Route path="dancers" element={<DancersList />} />
      <Route path="competitions" element={<Competitions />} />
      <Route path="competitions/:id" element={<KeyedById component={CompetitionDetail} />} />
      <Route path="choreographies" element={<ChoreographiesList />} />
      <Route path="choreographies/:id" element={<KeyedById component={ChoreographyDetail} />} />
      <Route path="qualified" element={<QualifiedChoreographies />} />
      <Route path="invoices" element={<InvoiceManagement />} />
    </Route>

    {/* Admin area: one layout route, pages loaded on demand */}
    <Route
      path="/admin"
      element={
        <RequireAuth roles={['admin']}>
          <AdminLayout>
            <Suspense fallback={<Spinner label="Načítání…" />}>
              <Outlet />
            </Suspense>
          </AdminLayout>
        </RequireAuth>
      }
    >
      <Route index element={<AdminDashboard />} />
      <Route path="groups" element={<GroupsManagement />} />
      <Route path="groups/:id" element={<GroupDetail />} />
      <Route path="competitions" element={<CompetitionsManagement />} />
      <Route path="logos" element={<LogoManagement />} />
      <Route path="media" element={<MediaManagement />} />
      <Route path="choreographies/:id" element={<AdminChoreographyDetail />} />
      <Route path="export" element={<ExportPrint />} />
      <Route path="early-bird-report" element={<EarlyBirdReport />} />
      <Route path="invoices" element={<AdminInvoiceManagement />} />
      <Route path="invoices/:id" element={<AdminInvoiceDetail />} />
      <Route path="invoice-settings" element={<InvoiceSettings />} />
      <Route path="overview" element={<FeatureRoute name="overview"><CompetitionOverview /></FeatureRoute>} />
      <Route path="startlist" element={<FeatureRoute name="running_order"><RunningOrder /></FeatureRoute>} />
      <Route path="qualification" element={<FeatureRoute name="qualification"><Qualification /></FeatureRoute>} />
      <Route path="statistics" element={<FeatureRoute name="statistics"><TourStatistics /></FeatureRoute>} />
      <Route path="outbox" element={<Outbox />} />
      <Route path="prirucka" element={<Guide />} />
    </Route>

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <MetaProvider>
    <AuthProvider>
      <UploadProvider>
        <ScrollToTop />
        <StagingBanner />
        <ImpersonationBanner />
        <MaintenanceBanner />
        <AppRoutes />
      </UploadProvider>
    </AuthProvider>
  </MetaProvider>
);

export default App;
